/**
 * When a transient overlay in the shell should close.
 *
 * The predicates are pure and DOM-agnostic on purpose: the shell's test suite
 * runs in `environment: "node"` with no jsdom and no `@testing-library/react`,
 * so behaviour that lives only inside an effect is behaviour nothing can
 * assert. Everything that decides *whether* to close lives here, and the hook
 * in `useDismissable.ts` is left as thin wiring over these.
 *
 * The whole set exists because Esc was the ONLY way to close the module search
 * panel: clicking the page behind it, tabbing past it, or navigating elsewhere
 * all left it hanging over the shell. Esc is the keyboard escape hatch, not the
 * dismissal contract.
 */

/**
 * Whether `target` is `container` or sits inside it.
 *
 * Asks the container, rather than type-guarding the target with
 * `target instanceof Node`. That guard reads as the careful option and is the
 * opposite: `Node` is a browser global that does not exist under
 * `environment: "node"`, so the guard throws `ReferenceError` in the very test
 * run that is meant to prove the predicate — and would do the same in any
 * server-rendered path. Containment is the container's question anyway.
 */
export function containsTarget(container: HTMLElement | null, target: EventTarget | null): boolean {
  if (!container || !target) return false;
  if (container === target) return true;
  return typeof container.contains === "function" && container.contains(target as Node);
}

/**
 * Whether a pointer press should close the overlay.
 *
 * **This is the one that has to be right.** The obvious implementation —
 * "close on any mousedown while open" — fires BEFORE the browser resolves the
 * click, so the panel unmounts out from under the result the person was
 * pressing and the click lands on whatever is now underneath. The result
 * becomes unclickable by mouse while remaining clickable by keyboard, which
 * reads as a broken link rather than a dismissal bug.
 *
 * Containment is what avoids it: a press that landed inside the overlay is not
 * a dismissal at all, so the panel survives long enough for `click` to fire on
 * the result and for the result's own handler to close it.
 */
export function shouldDismissOnPointer(
  container: HTMLElement | null,
  target: EventTarget | null,
): boolean {
  return !containsTarget(container, target);
}

/**
 * Whether focus leaving the overlay should close it.
 *
 * `nextFocus` is the `focusout` event's `relatedTarget` — the element about to
 * receive focus. Tab-away lands somewhere outside and closes; moving between
 * the field and a result stays inside and does not.
 *
 * A `null` relatedTarget means focus went nowhere the document can name — the
 * window lost focus, or the person clicked bare page. Closing on that would
 * dismiss the panel every time someone switched to another application and
 * came back, so it is deliberately NOT a dismissal here; a click on bare page
 * is already covered by `shouldDismissOnPointer`.
 */
export function shouldDismissOnFocusOut(
  container: HTMLElement | null,
  nextFocus: EventTarget | null,
): boolean {
  if (nextFocus === null) return false;
  return !containsTarget(container, nextFocus);
}

/** The keyboard dismissal. Kept alongside the rest so the contract is in one place. */
export function isDismissKey(event: { key: string }): boolean {
  return event.key === "Escape";
}

/**
 * Whether a route change should close the overlay.
 *
 * Compares the pathname the overlay opened at against the current one. A
 * navigation to the SAME path (a re-render, a search-param-only change) is not
 * a route change and must not close the panel mid-interaction.
 */
export function shouldDismissOnRoute(openedAt: string | null, current: string): boolean {
  if (openedAt === null) return false;
  return openedAt !== current;
}
